import { computed, inject } from '@angular/core';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { ClientSSO } from '../../../commons/entities/client-sso';
import Language from '../../../commons/entities/language';
import { EQueryStringParams, LocalStorageService } from '../../../commons/stores/local-storage.service';

type TvLoginState = {
  loaded: boolean; // has the TvLoginState list been loaded
  error: string | null; // last known error (if any)
  translationLoadedForTheFirstTime: boolean; // for showing labels only when translations are loaded
  portals: ClientSSO[]; // list of portals
  email: string; // entered email address
  selectedSystemLanguage: Language;
  customCss: boolean;
  userWasOnHomePage: boolean;
};

const initialState: TvLoginState = {
  loaded: false,
  translationLoadedForTheFirstTime: false,
  error: null,
  email: '',
  portals: [],
  selectedSystemLanguage: new Language(),
  customCss: false,
  userWasOnHomePage: false,
};

export const TvLoginStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withComputed(({ portals, selectedSystemLanguage }) => ({
    selectedSystemLanguageId: computed(() => selectedSystemLanguage()?.id),

    sortedPortals: computed(() => [...portals()].sort((a, b) => a.alias.localeCompare(b.alias))),
  })),
  withMethods((store, localStorageService = inject(LocalStorageService)) => ({
    translationLoadedForTheFirstTimeSuccessfully(): void {
      patchState(store, (state) => ({ ...state, translationLoadedForTheFirstTime: true }));
    },

    translationReset(): void {
      patchState(store, (state) => ({ ...state, translationLoadedForTheFirstTime: false }));
    },

    setEmail(email: string) {
      patchState(store, (state) => ({ ...state, email }));
      localStorageService.putEmail(email);
    },

    getEmailFromLocalStorage(): string {
      return localStorageService.getEmail() || '';
    },

    setPortals(portals: ClientSSO[]) {
      patchState(store, (state) => ({ ...state, portals }));
    },

    setCustomCss(customCss: boolean) {
      patchState(store, (state) => ({ ...state, customCss }));
    },

    setUserWasOnHomePage(userWasOnHomePage: boolean) {
      patchState(store, (state) => ({ ...state, userWasOnHomePage }));
    },

    setSelectedSystemLanguage(selectedSystemLanguage: Language) {
      patchState(store, (state) => ({ ...state, selectedSystemLanguage }));
    },

    setSelectedSystemLanguageAndPutItToStore(selectedSystemLanguage: Language) {
      localStorageService.putSelectedLanguageToStore(selectedSystemLanguage.id);
      patchState(store, (state) => ({ ...state, selectedSystemLanguage }));
    },

    getSelectedLanguageId(): string {
      const langIdFromStore = localStorageService.getSelectedLanguageFromStore();
      const langId = String(this.selectedSystemLanguageId());

      if (langIdFromStore !== String(langId)) {
        /* console.error(
          'Selected language ID in local storage does not match the one in the store. Local storage ID: ',
          langIdFromStore,
          'Store ID: ',
          langId
        ); */
        return langIdFromStore;
      }

      return langId;
    },

    setCSRFToken(token: string) {
      localStorageService.putCSRFToken(token);
    },

    getCSRFToken(): string {
      return localStorageService.getCSRFToken();
    },

    putReturnUrl(returnUrl: string) {
      localStorageService.putReturnUrl(returnUrl);
    },

    putUrlParameterToStorage(key: EQueryStringParams, value: unknown): void {
      localStorageService.putQueryParamToLocalStorage(key, value);
    },

    clearUrlParamsInLocalStorage(): void {
      localStorageService.clearQueryParamsFromLocalStorage();
    },

    getUrlParameterClientAlias(): string {
      return localStorageService.getQueryParamFromLocalStorage(EQueryStringParams.clientAlias);
    },

    putClientAlias(alias: string): void {
      if (alias) {
        localStorageService.saveClientAliasForMobile(alias);
      } else {
        localStorageService.removeClientAliasForMobile();
      }
    },

    resetUrlParameterClientAlias(): void {
      localStorageService.removeQueryParamFromLocalStorage(EQueryStringParams.clientAlias);
    },

    getLoginAlias(): string {
      return localStorageService.getClientAliasForMobile();
    },

    setUrlParameterClientAlias(clientAlias: string): void {
      localStorageService.putQueryParamToLocalStorage(EQueryStringParams.clientAlias, clientAlias);
    },

    getUrlParameterIsMobileApp(): boolean {
      return !!localStorageService.getQueryParamFromLocalStorage(EQueryStringParams.isMobile);
    },

    getUrlParameterAppVer(): string {
      return localStorageService.getQueryParamFromLocalStorage(EQueryStringParams.appVer);
    },

    getUrlParameterReturnUrl(): string {
      return localStorageService.getQueryParamFromLocalStorage(EQueryStringParams.returnUrl);
    },
  }))
);
